import { Component, OnInit, Input, ViewChild, inject } from "@angular/core";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { RendererType } from "@enums/RendererType";
import { FormatType } from "@enums/FormatType";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { MoiDataService } from "../services/moi-data.service";
import { MoiTypes } from "@enums/moi/MoiTypes";
import { LinkedMoiModel } from "@models/moi/LinkedMoiModel";
import { ToastrService } from "ngx-toastr";
import { CerrixTableComponent } from "@app/shared/cerrix-table/cerrix-table.component";
import { MoiConfig } from "@models/moi/MoiConfig";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { nameof } from "@methods/CommonMethods";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { firstValueFrom, forkJoin, from } from "rxjs";
import { injectTranslation, TranslatePipe, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/moi/i18n/scope";

@Component({
    selector: "linked-moi-overview",
    templateUrl: "./moi-linked-overview.component.html",
    styleUrls: ["./moi-linked-overview.component.scss"],
    imports: [TranslatePipe, CerrixTableComponent],
    viewProviders: [provideTranslationScope(scope)],
})
export class LinkedMoiOverviewComponent implements OnInit {
    private tabService = inject(TabService);
    private _moiDS = inject(MoiDataService);
    private pages = inject(Pages);
    private _toastr = inject(ToastrService);
    private _promptService = inject(CerrixPromptService);

    @Input() moiType: MoiTypes;
    @Input() parentGuid: string;
    @Input() assessmentGuid: string;
    @Input() useLazyLoading = true;

    @Input() canAdd: boolean;
    @Input() canDelete: boolean;

    @ViewChild("cerrixTableComponent", { static: true }) cerrixTableComponent: CerrixTableComponent;

    rendererConfig: RendererConfig[];
    overviewConfig: GenericListConfig;
    headerLookup: any = {};
    linkedMois: LinkedMoiModel[];
    activeRow: any = null;

    numberProps: string[] = ["Progress"];
    dateProps: string[] = ["Date status changed", "Due Date"];

    private _translation = injectTranslation(scope);

    constructor() {
        this.overviewConfig = <GenericListConfig>{
            idProp: "guid",
            fields: [
                {
                    fieldName: nameof<LinkedMoiModel>("id"),
                    hideInOverview: true,
                },
                {
                    fieldName: nameof<LinkedMoiModel>("guid"),
                    hideInOverview: true,
                },
                {
                    fieldName: nameof<LinkedMoiModel>("identifier"),
                    overviewSortOrder: 0,
                },
                {
                    fieldName: nameof<LinkedMoiModel>("name"),
                    overviewSortOrder: 1,
                },
                {
                    fieldName: nameof<LinkedMoiModel>("organizationResponsible"),
                    overviewSortOrder: 3,
                },
                {
                    fieldName: nameof<LinkedMoiModel>("status"),
                    overviewSortOrder: 4,
                },
                {
                    fieldName: nameof<LinkedMoiModel>("dateStatusChanged"),
                    overviewSortOrder: 5,
                },
                {
                    fieldName: nameof<LinkedMoiModel>("responsible"),
                    overviewSortOrder: 6,
                },
                {
                    fieldName: nameof<LinkedMoiModel>("dueDate"),
                    overviewSortOrder: 7,
                },
                {
                    fieldName: nameof<LinkedMoiModel>("isOverdue"),
                    overviewSortOrder: 8,
                },
                {
                    fieldName: nameof<LinkedMoiModel>("progress"),
                    overviewSortOrder: 9,
                },
            ],
        };

        this.rendererConfig = [
            {
                textColumn: nameof<LinkedMoiModel>("dateStatusChanged"),
                type: RendererType.Default,
                formatType: FormatType.DateFormat,
            },
            {
                textColumn: nameof<LinkedMoiModel>("dueDate"),
                actionColumn: nameof<LinkedMoiModel>("isOverdue"),
                actionValue: "Yes",
                type: RendererType.DueDate,
                formatType: FormatType.DateFormat,
            },
            {
                textColumn: nameof<LinkedMoiModel>("progress"),
                type: RendererType.Progress,
            },
        ];
    }

    ngOnInit() {
        if (!this.useLazyLoading) {
            this.load(true);
        }
    }

    openMoi(moi: LinkedMoiModel) {
        if (moi.canOpen === true) {
            this.generateMoiTab(moi);
        } else if (moi.canOpen === false) {
            this.showUnableToOpenWarning();
        } else {
            this._moiDS.hasAccessToMoi(moi.guid).subscribe((hasAccess) => {
                // Store the result in the moi
                if (hasAccess) {
                    moi.canOpen = true;

                    this.generateMoiTab(moi);
                } else {
                    moi.canOpen = false;

                    this.showUnableToOpenWarning();
                }
            });
        }
    }

    private generateMoiTab(moi: LinkedMoiModel) {
        this.tabService.generateTab(
            this.pages.MoiDetail,
            moi.guid,
            this._translation.t("linkedMoiOverview.openMoiTabTitle", {
                // Pass the identifier as a string: ICU would group-format a number.
                identifier: `${+moi.identifier}`,
                name: moi.name,
            })
        );
    }

    private showUnableToOpenWarning() {
        this._toastr.warning(this._translation.t("linkedMoiOverview.notEnoughRightsMoi"));
    }

    async addMoi() {
        await this._translation.ready();
        const sourceID = await firstValueFrom(this._moiDS
            .getSourceIdByGuid(this.moiType, this.parentGuid));
        if (!sourceID || sourceID <= 0) {
            this._toastr.error(this._translation.t("linkedMoiOverview.sourceNotFound"));
            return;
        }

        const config = <MoiConfig>{
            MoiType: this.moiType,
            ParentId: sourceID,
            ParentGuid: this.parentGuid,
            AssessmentGuid: this.assessmentGuid,
        };

        this.tabService.generateTab(
            this.pages.MoiDetail,
            null,
            this._translation.t("linkedMoiOverview.newMoiTabTitle"),
            config
        );
    }

    async deleteMoi() {
        await this._translation.ready();
        if (this.activeRow == null) {
            this._toastr.error(this._translation.t("linkedMoiOverview.selectMoiFirst"), this._translation.t("linkedMoiOverview.unableToDeleteMoi"));
            return;
        }

        const moi = this.activeRow as LinkedMoiModel;
        this._promptService
            .confirm(
                this._translation.t("linkedMoiOverview.deleteMoiTitle"),
                this._translation.t("linkedMoiOverview.deleteMoiConfirmBody", {
                    identifier: moi.identifier,
                    name: moi.name,
                })
            )
            .onConfirm()
            .subscribe(() => {
                this._moiDS.deleteMoi(moi.guid).subscribe((x) => {
                    this.load(true);
                });
            });
    }

    load(refresh: boolean): void {
        if (this.linkedMois && !refresh) {
            return;
        }

        this.linkedMois = null;
        this.activeRow = null;

        // The header labels are translated below, so wait for the scope alongside the data.
        forkJoin([
            this._moiDS.getLinkedMois(this.assessmentGuid || this.parentGuid, this.moiType),
            from(this._translation.ready()),
        ]).subscribe(([d]) => {
            this.linkedMois = d;

            this.setFieldLabels();
            this.setHeaders();
            this.cerrixTableComponent.setData(this.linkedMois);
        });
    }

    private setFieldLabels() {
        const labelKeys: Partial<Record<keyof LinkedMoiModel, string>> = {
            name: "nameHeader",
            organizationResponsible: "organizationResponsibleHeader",
            status: "statusHeader",
            dateStatusChanged: "dateStatusChangedHeader",
            responsible: "responsibleHeader",
            dueDate: "dueDateHeader",
            isOverdue: "isOverdueHeader",
            progress: "progressHeader",
        };
        this.overviewConfig.fields.forEach((field) => {
            const key = labelKeys[field.fieldName];
            if (key) {
                field.prettyName = this._translation.t(`linkedMoiOverview.${key}`);
            }
        });
    }

    private setHeaders() {
        if (this.linkedMois && this.linkedMois.length > 0) {
            Object.getOwnPropertyNames(this.linkedMois[0]).forEach((key) => {
                if (!this.headerLookup[key]) {
                    this.headerLookup[key] = key;
                }
            });
        }
    }
}
